/**
 * The pure parts of the clicks report — the heat ramp, and the two facts the
 * scroll list is read for.
 *
 * Split out of HeatmapView so both can be asserted directly: a ramp that drifts
 * from the legend drawn beside it, or a cliff marked between the wrong two
 * bands, is wrong in a way a screenshot does not catch.
 */

/**
 * Blue → teal → amber → red, the conventional heat ramp.
 *
 * A rainbow is the wrong choice for magnitude everywhere else in this product,
 * and it is the right one here for the same reason road maps use it: this is not
 * a chart being read off an axis, it is a field being scanned for its hottest
 * point, and people already know what a heat map means.
 */
export const RAMP: [number, [number, number, number]][] = [
  [0, [59, 76, 192]],
  [0.35, [20, 160, 160]],
  [0.65, [232, 163, 61]],
  [1, [181, 35, 80]],
];

export function rampAt(t: number): [number, number, number] {
  const clamped = Math.max(0, Math.min(1, Number(t) || 0));
  for (let index = 1; index < RAMP.length; index++) {
    const [stop, colour] = RAMP[index];
    if (clamped <= stop) {
      const [prevStop, prevColour] = RAMP[index - 1];
      const span = stop - prevStop || 1;
      const ratio = (clamped - prevStop) / span;
      return [
        Math.round(prevColour[0] + (colour[0] - prevColour[0]) * ratio),
        Math.round(prevColour[1] + (colour[1] - prevColour[1]) * ratio),
        Math.round(prevColour[2] + (colour[2] - prevColour[2]) * ratio),
      ];
    }
  }
  return RAMP[RAMP.length - 1][1];
}

/**
 * The legend's gradient, generated from the ramp the canvas actually paints.
 * Hand-writing the same four colours in CSS is how a legend ends up describing a
 * picture that has since been re-tuned.
 */
export const rampGradient = (): string =>
  `linear-gradient(90deg, ${RAMP.map(
    ([stop, [r, g, b]]) => `rgb(${r} ${g} ${b}) ${Math.round(stop * 100)}%`,
  ).join(', ')})`;

export interface ScrollBand {
  bucket: number;
  share: number;
}

/**
 * The deepest band at least half of the readers reached.
 *
 * `null` when the very first band is already under half — a page whose top
 * tenth loses most of its readers has no median depth worth naming, and
 * pretending otherwise would put the label above the fold.
 */
export const medianDepth = (rows: ScrollBand[]): number | null => {
  let deepest: number | null = null;
  for (const row of rows) if (row.share >= 0.5) deepest = row.bucket;
  return deepest;
};

/**
 * Where the page loses most of its readers: the largest fall between two
 * ADJACENT bands, returned as the band the fall lands on.
 *
 * The article the caveat asks the reader to look for is exactly this, and
 * eyeballing it off ten bars is the part people get wrong. Ties go to the
 * shallower of the two, because the first place a page loses its audience is
 * the one worth fixing. `null` when nothing falls — a page nobody scrolls at
 * all is flat, and a cliff drawn on it would be noise.
 */
export const biggestDrop = (rows: ScrollBand[]): number | null => {
  let at: number | null = null;
  let worst = 0;
  for (let index = 1; index < rows.length; index++) {
    const fall = rows[index - 1].share - rows[index].share;
    // Strictly greater by more than rounding noise. Shares are fractions, and
    // three equal falls of a fifth subtract to 0.19999999999999996,
    // 0.20000000000000007 and 0.19999999999999998 — without the tolerance the
    // cliff lands on whichever band floating point happened to favour.
    if (fall > worst + 1e-9) {
      worst = fall;
      at = rows[index].bucket;
    }
  }
  return worst > 1e-9 ? at : null;
};
